/**
 * Select an athlete to compare with. Lists users and filters by name.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { listUsers } from '../../domain/usecases/profile';
import Avatar from '../../shared/ui/Avatar.jsx';
import Card from '../../shared/ui/Card.jsx';
import Body from '../../shared/ui/Body.jsx';
import Display from '../../shared/ui/Display.jsx';
import EmptyState from '../../shared/ui/EmptyState.jsx';
import Input from '../../shared/ui/Input.jsx';
import Skeleton from '../../shared/ui/Skeleton.jsx';
import styles from './CompareSelectPage.module.css';

export function CompareSelectPage() {
  const { userId: myId } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listUsers(150).then((list) => {
      setUsers(list.filter((u) => u.id !== myId));
      setLoading(false);
    });
  }, [myId]);

  const filtered = filter.trim()
    ? users.filter((u) => (u.displayName || '').toLowerCase().includes(filter.trim().toLowerCase()))
    : users;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <Link to="/profile" className={styles.back}>← Profile</Link>
        <Display as="h1" size="l" style={{ marginBottom: '0.25rem' }}>Compare</Display>
        <Body tone="secondary">Select someone to compare your PRs and recent lifts.</Body>
      </div>

      <Input
        label="SEARCH"
        placeholder="Search by name..."
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        type="search"
        aria-label="Search athletes"
      />

      {loading ? (
        <div className={styles.skeletonList}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} height={60} radius="var(--radius-md)" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon="search"
          title="No athletes found"
          body="Try a different name."
        />
      ) : (
        <ul className={styles.list}>
          {filtered.map((u) => (
            <li key={u.id}>
              <button
                type="button"
                className={styles.rowWrap}
                onClick={() => navigate(`/compare/${u.id}`)}
              >
                <Card padding="sm" className={styles.row}>
                  <Avatar
                    src={u.avatarUrl || undefined}
                    initials={u.displayName?.charAt(0)?.toUpperCase() || '?'}
                    size="md"
                  />
                  <Body as="span" style={{ flex: 1, fontWeight: 500 }}>
                    {u.displayName || 'Athlete'}
                  </Body>
                  <span className={styles.arrow}>→</span>
                </Card>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
